import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HiPlay } from "react-icons/hi2";
import { HiArrowTopRightOnSquare } from "react-icons/hi2";

const VideoTestimonialCard = ({
  id,
  title = "Student Experience",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const thumbnailUrl = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;

  const openYouTube = () => {
    window.open(
      `https://www.youtube.com/watch?v=${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      whileHover={shouldReduceMotion ? undefined : { y: -6 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="
        group relative overflow-hidden
        rounded-[24px]
        border border-gray-200/80
        bg-white
        shadow-[0_8px_30px_rgba(15,23,42,0.06)]
        transition-all duration-300
        hover:border-gray-300
        hover:shadow-[0_18px_45px_rgba(15,23,42,0.12)]
        dark:border-gray-700/70
        dark:bg-gray-900
        dark:hover:border-gray-600
      "
    >
      {/* Premium top accent */}
      <div
        aria-hidden="true"
        className="
          absolute inset-x-0 top-0 z-20 h-[2px]
          bg-gradient-to-r
          from-transparent
          via-red-500
          to-transparent
          opacity-70
        "
      />

      {/* Video */}
      <div
        className="
          relative aspect-[16/10]
          overflow-hidden
          bg-gray-100
          dark:bg-gray-950
        "
      >
        {!isPlaying ? (
          <button
            type="button"
            onClick={() => setIsPlaying(true)}
            aria-label={`Play ${title}`}
            className="
              relative block
              h-full w-full
              cursor-pointer
              overflow-hidden
              border-0
              bg-transparent
              p-0
              text-left
              focus:outline-none
              focus-visible:ring-2
              focus-visible:ring-red-500
              focus-visible:ring-offset-2
              dark:focus-visible:ring-offset-gray-900
            "
          >
            {/* Thumbnail */}
            <img
              src={thumbnailUrl}
              alt=""
              loading="lazy"
              className="
                h-full w-full
                object-cover
                object-center
                transition-transform
                duration-700
                ease-out
                group-hover:scale-[1.045]
              "
            />

            {/* Soft overlay */}
            <span
              aria-hidden="true"
              className="
                absolute inset-0
                bg-gradient-to-t
                from-black/65
                via-black/15
                to-black/5
              "
            />

            {/* Top metadata */}
            <span
              aria-hidden="true"
              className="
                absolute left-4 top-4
                inline-flex items-center
                rounded-full
                border border-white/15
                bg-black/45
                px-2.5 py-1
                text-[10px] font-semibold
                uppercase tracking-[0.12em]
                text-white
                backdrop-blur-md
              "
            >
              Student Story
            </span>

            {/* Play button */}
            <span
              aria-hidden="true"
              className="
                absolute left-1/2 top-1/2
                flex h-16 w-16
                -translate-x-1/2
                -translate-y-1/2
                items-center justify-center
                rounded-full
                border border-white/50
                bg-white/95
                text-red-600
                shadow-[0_10px_35px_rgba(0,0,0,0.25)]
                transition-all duration-300
                group-hover:scale-110
                group-hover:bg-white
                sm:h-[68px] sm:w-[68px]
              "
            >
              <HiPlay className="ml-0.5 text-2xl sm:text-[26px]" />
            </span>

            {/* Bottom visual cue */}
            <span
              aria-hidden="true"
              className="
                absolute bottom-4 left-4
                flex items-center gap-2
                text-[11px] font-medium
                text-white/90
              "
            >
              <span
                className="
                  h-1.5 w-1.5
                  rounded-full
                  bg-white
                "
              />
              Watch experience
            </span>
          </button>
        ) : (
          <div className="relative h-full w-full">
            <iframe
              src={embedUrl}
              title={title}
              className="h-full w-full"
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />

            {/* Open on YouTube */}
            <button
              type="button"
              onClick={openYouTube}
              aria-label={`Open ${title} on YouTube`}
              className="
                absolute right-3 top-3
                inline-flex items-center gap-1.5
                rounded-full
                border border-white/20
                bg-black/60
                px-2.5 py-1.5
                text-[10px] font-semibold
                text-white
                backdrop-blur-md
                transition-colors
                hover:bg-black/80
                focus:outline-none
                focus-visible:ring-2
                focus-visible:ring-white
              "
            >
              YouTube
              <HiArrowTopRightOnSquare className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-5 py-4 sm:px-5 sm:py-[18px]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3
              className="
                line-clamp-2
                text-[15px]
                font-semibold
                leading-6
                text-gray-900
                dark:text-white
              "
            >
              {title}
            </h3>

            <p
              className="
                mt-1.5
                text-xs
                leading-5
                text-gray-500
                dark:text-gray-400
              "
            >
              Watch how learners experienced their journey with IA4Solutions.
            </p>
          </div>

          {/* Small play indicator */}
          <span
            aria-hidden="true"
            className="
              mt-0.5
              flex h-8 w-8
              shrink-0
              items-center justify-center
              rounded-full
              bg-red-50
              text-red-600
              transition-transform duration-300
              group-hover:scale-105
              dark:bg-red-500/10
              dark:text-red-400
            "
          >
            <HiPlay className="text-sm" />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export default VideoTestimonialCard;